import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { getTelecommunicationsData, saveTelecommunicationsForm } from '../../../../../../api/transitions';
import { enqueueSnackbar } from '../../../../../../reusable-components/snackbar';
import { trimData } from '../../../../../../utils/formatText';
import SkeletonBlock from '../../../SkeletonBlock';
import { TELECOMMUNICATIONS } from '../../../formQuestionsEnum';
import ITForm from './ITForm';
import InternetForm from './InternetForm';
import PhoneForm from './PhoneForm';
import TVForm from './TVForm';

const TelecommunicationsView = forwardRef(({ setFormData }, ref) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('facilityId');
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const userPermissions = useSelector((state) => state.transitions?.data?.userPermissions);

  const telecommunicationsPermissions = Object.fromEntries(
    Object.values(userPermissions?.[id]?.[facilityId]?.telecommunicationsFieldPermissions || {}).map((x) => [
      x.formQuestionId,
      x.isEditable,
    ])
  );

  useEffect(() => {
    getTelecommunicationsData(id, facilityId ?? 0)
      .then((resp) => {
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to load Telecommunications data', { variant: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, facilityId]);

  useEffect(() => {
    const fieldNameMap = Object.fromEntries(Object.entries(TELECOMMUNICATIONS).map(([, v]) => [v.id, v.fieldName]));

    const mappedFields = {
      ...data?.fields?.reduce((acc, field) => ({ ...acc, [fieldNameMap[field.formQuestionId]]: field.answer }), {}),
    };

    setFormData({ transitionFacilityId: data?.transitionFacilityId, ...mappedFields });
  }, [data]);

  const submitForm = async (allData) => {
    const flattenData = Object.entries(allData).reduce(
      (acc, field) => ({ ...acc, [field[0]]: field[1]?.vendor !== undefined ? field[1]?.vendor : field[1] }),
      {}
    );
    const result = trimData(flattenData);

    await saveTelecommunicationsForm({
      id,
      sellerFacilityId: facilityId,
      ...result,
    })
      .then((resp) => {
        enqueueSnackbar('Telecommunications data saved successfully', { variant: 'success' });
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to save Telecommunications data', { variant: 'error' });
      });
  };

  useImperativeHandle(ref, () => ({ submitForm }));

  return isLoading ? (
    <SkeletonBlock height={600} sx={{ mt: 2 }} />
  ) : (
    <>
      <PhoneForm permissions={telecommunicationsPermissions} />
      <InternetForm permissions={telecommunicationsPermissions} />
      <TVForm permissions={telecommunicationsPermissions} />
      <ITForm permissions={telecommunicationsPermissions} />
    </>
  );
});

export default TelecommunicationsView;

TelecommunicationsView.propTypes = {
  setFormData: PropTypes.func,
};
